import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { dropTask } from 'ember-concurrency';
import { pluralize } from 'ember-inflector';
import { createURN } from 'garaje/models/generic-resource';
import type AjaxFetchService from 'garaje/services/ajax-fetch';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type StateService from 'garaje/services/state';
import { ResourceBookingRule, type ResourceTypeConfig } from 'garaje/types/generic-resources/ResourceTypeConfig';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';
import { hourOptions } from 'garaje/utils/hour-options';
import urlBuilder from 'garaje/utils/url-builder';

const DEFAULT_BOOKING_WINDOW_OPTION = { value: '07:00', label: '7:00 am' };
interface BookingLimitRulePanelArgs {
  resourceTypeId: number;
  resourceTypeConfig: ResourceTypeConfig;
}

interface AdvancedBookingLimitOption {
  offset: number | null;
  label: string;
}
interface BookingWindowOption {
  value: string;
  label: string;
}

export default class BookingLimitRulePanel extends Component<BookingLimitRulePanelArgs> {
  @service declare state: StateService;
  @service declare flashMessages: FlashMessagesService;
  @service declare ajax: AjaxFetchService;

  @tracked advancedBookingOptions: AdvancedBookingLimitOption[];
  @tracked selectedAdvancedBookingOption: AdvancedBookingLimitOption | null;
  @tracked selectedBookingWindowOption: BookingWindowOption | null = DEFAULT_BOOKING_WINDOW_OPTION;
  @tracked isDirty: boolean = false;

  constructor(owner: unknown, args: BookingLimitRulePanelArgs) {
    super(owner, args);

    this.advancedBookingOptions = [
      { offset: null, label: 'As far as possible' },
      { offset: 0, label: 'Today only' },
      { offset: 1, label: 'Today & tomorrow' },
    ];

    for (let weekOffset = 1; weekOffset < 13; weekOffset++) {
      this.advancedBookingOptions.push({
        offset: weekOffset * 7,
        label: `${pluralize(weekOffset, 'week')} in advance`,
      });
    }

    const selectedAdvancedBookingOption =
      this.resourceOffset === undefined
        ? this.advancedBookingOptions[0]
        : this.advancedBookingOptions.find((o) => o.offset === this.resourceOffset?.daysInAdvance);
    this.selectedAdvancedBookingOption = selectedAdvancedBookingOption || null;

    if (this.resourceOffset && this.resourceOffset.daysInAdvance !== null) {
      this.selectedBookingWindowOption = this.resourceOffset.bookingWindow
        ? {
            value: this.resourceOffset.bookingWindow,
            label:
              this.bookingWindowOptions.find((o) => o.value === this.resourceOffset?.bookingWindow)?.label ||
              this.resourceOffset.bookingWindow,
          }
        : DEFAULT_BOOKING_WINDOW_OPTION;
    }
  }

  get resourceOffset():
    | { daysInAdvance: number; bookingWindow: string | null }
    | { daysInAdvance: null; bookingWindow: null }
    | undefined {
    const bookingRules = this.args.resourceTypeConfig?.bookingRules;
    const advancedBookingRule = bookingRules.find((b) => b.key === ResourceBookingRule.ADVANCED_BOOKING);

    if (!advancedBookingRule) {
      return undefined;
    }

    if (!advancedBookingRule.properties) {
      return undefined;
    }

    const { daysInAdvance, bookingWindowOpensAt } = advancedBookingRule.properties;

    // The booking window should only exist if we set a limit to the number of days in advance a reservation can be made
    if (daysInAdvance === null) {
      return { daysInAdvance: null, bookingWindow: null };
    }

    return {
      daysInAdvance,
      bookingWindow: bookingWindowOpensAt,
    };
  }

  get bookingWindowOptions(): BookingWindowOption[] {
    return hourOptions(30, false, 'HH:mm');
  }

  get isBookingWindowDisabled(): boolean {
    return this.selectedAdvancedBookingOption?.offset === null;
  }

  get hasChanges(): boolean {
    const isDaysInAdvanceDirty =
      this.resourceOffset === undefined
        ? true
        : this.selectedAdvancedBookingOption?.offset !== this.resourceOffset.daysInAdvance;

    const isBookingWindowDirty =
      this.resourceOffset === undefined
        ? true
        : this.selectedBookingWindowOption?.value !== this.resourceOffset.bookingWindow;

    return isDaysInAdvanceDirty || isBookingWindowDirty;
  }

  @action
  handleSelectBookingWindowOption(option: string): void {
    const newBookingWindow = this.bookingWindowOptions.find((_option) => _option.value === option);
    this.selectedBookingWindowOption = newBookingWindow || null;

    this.isDirty = this.hasChanges;
  }

  @action
  handleSelectAdvancedBookingOption(offset: number | null): void {
    const newOffset = this.advancedBookingOptions.find((o) => o.offset === offset);
    this.selectedAdvancedBookingOption = newOffset || null;

    this.isDirty = this.hasChanges;
  }

  saveTask = dropTask(async () => {
    try {
      const resourceTypeConfig = this.args.resourceTypeConfig;
      const offset = this.selectedAdvancedBookingOption?.offset;
      const daysInAdvance = typeof offset === 'number' ? offset : null;
      const bookingWindow = daysInAdvance === null ? null : this.selectedBookingWindowOption?.value;

      const url = urlBuilder.rms.resourceTypes();

      const newBookingRules = [
        ...resourceTypeConfig.bookingRules.filter((b) => b.key !== ResourceBookingRule.ADVANCED_BOOKING),
        {
          key: ResourceBookingRule.ADVANCED_BOOKING,
          type: ResourceBookingRule.ADVANCED_BOOKING,
          properties: {
            daysInAdvance,
            bookingWindowOpensAt: bookingWindow,
          },
        },
      ];

      await this.ajax.request(url, {
        method: 'PUT',
        contentType: 'application/json',
        data: JSON.stringify({
          data: {
            ...resourceTypeConfig,
            relationId: createURN('location', this.state.currentLocation.id),
            bookingRules: newBookingRules,
          },
        }),
      });

      const bookingRules = this.args.resourceTypeConfig.bookingRules;
      const advancedBookingRule = bookingRules.find((b) => b.key === ResourceBookingRule.ADVANCED_BOOKING);

      if (!advancedBookingRule) {
        return;
      }

      advancedBookingRule.properties.daysInAdvance = daysInAdvance;
      advancedBookingRule.properties.bookingWindowOpensAt = bookingWindow || null;

      this.isDirty = false;

      this.flashMessages.showAndHideFlash('success', 'Saved!');
    } catch (e) {
      this.flashMessages.showAndHideFlash('error', parseErrorForDisplay(e));
    }
  });
}
